import { useEffect, useState } from "react";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import KPICard from "@/components/KPICard";
import EmptyState from "@/components/EmptyState";
import {
    Building2,
    CheckSquare,
    AlertOctagon,
    CalendarRange,
    BedDouble,
    Sparkles,
} from "lucide-react";

function monthShift(month, delta) {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month) {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
}

export default function PortalReports() {
    const today = new Date();
    const initialMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const [month, setMonth] = useState(initialMonth);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/portal/reports/monthly?month=${month}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [month]);

    const downloadCsv = async () => {
        const res = await api.get(`/portal/reports/monthly.csv?month=${month}`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `zentra-informe-${month}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6" data-testid="portal-reports-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Informes mensuales
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Reporte de gestión
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1 capitalize">
                        {monthLabel(month)}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        className="zentra-btn-ghost"
                        onClick={() => setMonth(monthShift(month, -1))}
                        data-testid="portal-report-prev"
                    >
                        <ChevronLeft size={14} /> Anterior
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-ghost"
                        onClick={() => setMonth(initialMonth)}
                        data-testid="portal-report-current"
                    >
                        Mes actual
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-ghost"
                        onClick={() => setMonth(monthShift(month, 1))}
                        disabled={month >= initialMonth}
                        data-testid="portal-report-next"
                    >
                        Siguiente <ChevronRight size={14} />
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-primary"
                        onClick={downloadCsv}
                        disabled={!data}
                        data-testid="portal-report-download"
                    >
                        <Download size={14} /> Descargar CSV
                    </button>
                </div>
            </div>

            {loading || !data ? (
                <div className="zentra-skeleton h-72" />
            ) : data.properties_count === 0 ? (
                <EmptyState
                    icon={Building2}
                    title="Sin propiedades"
                    description="Tu gestor no ha asignado propiedades a tu portal todavía."
                />
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <KPICard
                            title="Propiedades"
                            value={data.properties_count}
                            suffix="en cartera"
                            icon={Building2}
                            accent
                            testId="report-kpi-properties"
                        />
                        <KPICard
                            title="Ocupación"
                            value={data.kpis.occupancy_pct}
                            suffix="%"
                            icon={BedDouble}
                            hint={`${data.kpis.nights_booked} noches reservadas`}
                            accent
                            testId="report-kpi-occupancy"
                        />
                        <KPICard
                            title="Limpiezas"
                            value={data.kpis.tasks_completed}
                            suffix={`/${data.kpis.tasks_total} completadas`}
                            icon={Sparkles}
                            hint={`${data.kpis.tasks_pending} pendientes`}
                            testId="report-kpi-cleanings"
                        />
                        <KPICard
                            title="Incidencias"
                            value={data.kpis.incidents_resolved}
                            suffix={`/${data.kpis.incidents_opened} resueltas`}
                            icon={AlertOctagon}
                            hint={data.kpis.incidents_open_now > 0 ? `${data.kpis.incidents_open_now} abiertas ahora` : "Todo cerrado"}
                            testId="report-kpi-incidents"
                        />
                    </div>

                    <div className="zentra-card-static overflow-hidden">
                        <div className="p-5 border-b border-zentra-border">
                            <h3 className="font-display font-semibold text-zentra-ink">
                                Desglose por propiedad
                            </h3>
                            <p className="text-xs text-zentra-muted mt-0.5">
                                Periodo: {data.from} → {data.to}
                            </p>
                        </div>
                        <table className="zentra-table" data-testid="report-table">
                            <thead>
                                <tr>
                                    <th>Propiedad</th>
                                    <th className="text-center">
                                        <CheckSquare size={11} className="inline mr-1" /> Limpiezas
                                    </th>
                                    <th className="text-center">
                                        <AlertOctagon size={11} className="inline mr-1" /> Incidencias
                                    </th>
                                    <th className="text-center">
                                        <CalendarRange size={11} className="inline mr-1" /> Noches
                                    </th>
                                    <th className="text-center">Ocupación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.per_property.map((p) => (
                                    <tr key={p.id} data-testid={`report-row-${p.code}`}>
                                        <td>
                                            <p className="font-medium text-zentra-ink">{p.name}</p>
                                            <p className="text-[10px] font-mono-zentra text-zentra-muted">{p.code}</p>
                                        </td>
                                        <td className="text-center">
                                            <span className="font-display font-semibold text-zentra-ink">
                                                {p.tasks_completed}
                                            </span>
                                            <span className="text-zentra-muted text-xs"> / {p.tasks_total}</span>
                                        </td>
                                        <td className="text-center">
                                            <span className="font-display font-semibold text-zentra-ink">
                                                {p.incidents_resolved}
                                            </span>
                                            <span className="text-zentra-muted text-xs"> / {p.incidents_opened}</span>
                                        </td>
                                        <td className="text-center font-mono-zentra text-sm">{p.nights_booked}</td>
                                        <td className="text-center">
                                            <div className="inline-flex flex-col items-center gap-1">
                                                <span className="text-sm font-semibold">{p.occupancy_pct}%</span>
                                                <div className="w-20 h-1.5 rounded-full bg-zentra-bg overflow-hidden">
                                                    <div
                                                        className="h-full bg-zentra-accent"
                                                        style={{ width: `${Math.min(100, p.occupancy_pct)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
